import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ApiMetric } from "@incendium/api";
import { useFormatMetric } from "features/analytics";
import { formatNumber } from "Helpers/numbers";
import { formatPercentage } from "Helpers/percentage";
import { useMemo } from "react";
import { PillWrapper } from "Components/ComparisonPill/PillBar";

export interface IMultipleComparisonItem {
  label: string;
  value: number;
  /** Optional colour for this segment (e.g. from AnalyticsContext colourMap for consistency across charts). */
  color?: string;
}

interface IMultipleComparisonPillProps {
  title: string;
  items: IMultipleComparisonItem[];
  metric?: ApiMetric;
  isPercentage?: boolean;
}

function MultipleComparisonPill({
  title,
  items,
  metric,
  isPercentage,
}: IMultipleComparisonPillProps) {
  const theme = useTheme();
  const formatMetric = useFormatMetric();

  const { percentages } = useMemo(() => {
    const total = items.reduce((sum, i) => sum + i.value, 0);
    if (total === 0) {
      const equal = items.length > 0 ? 100 / items.length : 0;
      return { percentages: items.map(() => equal) };
    }
    return {
      percentages: items.map((i) => (i.value / total) * 100),
    };
  }, [items]);

  const formatValue = useMemo(() => {
    if (metric) {
      return (v: number) => formatMetric(metric, v);
    }
    if (isPercentage) {
      return (v: number) => formatPercentage(v);
    }
    return (v: number) => formatNumber(v);
  }, [metric, isPercentage, formatMetric]);

  const chartColors = theme.palette.charts as Record<number, string>;

  if (items.length === 0) {
    return null;
  }

  const getColor = (item: IMultipleComparisonItem, index: number) =>
    item.color ?? chartColors[(index % 4) + 1] ?? chartColors[1];

  return (
    <Box py={0.75}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <PillWrapper>
        {items.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              background: getColor(item, index),
              minWidth: `${percentages[index]}%`,
              transition: "min-width 0.2s ease-in-out",
            }}
          />
        ))}
      </PillWrapper>
      <Stack
        direction="row"
        sx={{ width: "100%", mt: 0.25 }}
        justifyContent="flex-start"
      >
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          const justifyContent =
            items.length === 1
              ? "center"
              : isFirst
                ? "flex-start"
                : isLast
                  ? "flex-end"
                  : "center";
          return (
            <Box
              key={item.label}
              sx={{
                flex: `0 0 ${percentages[index]}%`,
                display: "flex",
                justifyContent,
                minWidth: 0,
                px: 0.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: getColor(item, index),
                  fontWeight: 600,
                }}
              >
                {formatValue(item.value)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default MultipleComparisonPill;
